import React, { useState } from 'react';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, SCREEN_PADDING_LARGE, TYPOGRAPHY } from '@/styles';
import { ImageViewerModal } from '@/components/image-viewer-modal';
import { openWebBrowserURL } from '@/utils/navigation';
import { OrderItemQuantityBox } from '@/components/order-item-quantity-box';
import { Platform, Text, TouchableOpacity, View } from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';

interface Props {
  formattedPrice: string | null;
  imageURLs?: string[];
  quantity: number;
  subtitle: React.ReactNode | null;
  title: string | null;
}

export const CreditRequestListItem: React.FC<Props> = ({
  formattedPrice,
  imageURLs,
  quantity,
  subtitle,
  title,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showViewer, setShowViewer] = useState(false);
  const { isLargeLayout } = useLayoutContext();
  const [styles] = useStyles();
  const isWeb = Platform.OS === 'web';

  const photos =
    imageURLs && imageURLs.length > 0 ? (
      <View style={styles.imagePreviews} testID="credit-request-item-images">
        {imageURLs.map((imageURL, index) => (
          <TouchableOpacity
            key={imageURL}
            onPress={() => {
              if (isWeb) {
                openWebBrowserURL(imageURL);
              } else {
                setActiveIndex(index);
                setShowViewer(true);
              }
            }}
          >
            <CachedImage
              alt={`Item image #${index}`}
              contentFit="cover"
              source={{ uri: imageURL }}
              style={styles.imagePreview}
              testID={`credit-request-item-image-${index}`}
            />
          </TouchableOpacity>
        ))}
      </View>
    ) : null;

  return (
    <>
      {isLargeLayout ? (
        <View style={styles.flex}>
          <View style={styles.mainContentLarge}>
            <View style={styles.quantityBoxWrapperLarge}>
              <OrderItemQuantityBox quantity={quantity} />
            </View>
            <View style={styles.titleDescriptionContainer}>
              <Text
                numberOfLines={2}
                style={[TYPOGRAPHY.medium, FONTS.primarySemiBold]}
              >
                {title}
              </Text>
            </View>
            {subtitle ? (
              <View style={styles.subtitleLarge}>{subtitle}</View>
            ) : null}
            <View style={styles.totalPriceLarge}>
              <Text style={TYPOGRAPHY.medium}>{formattedPrice}</Text>
            </View>
          </View>
          {photos}
        </View>
      ) : (
        <View style={styles.mainContent}>
          <View style={styles.quantityBoxWrapper}>
            <OrderItemQuantityBox quantity={quantity} />
          </View>
          <View style={styles.flex}>
            {title ? (
              <View style={styles.titleContainer}>
                <Text numberOfLines={2} style={FONTS.primarySemiBold}>
                  {title}
                </Text>
              </View>
            ) : null}
            <View style={styles.subtitle}>
              {subtitle}
              {photos}
            </View>
          </View>
          <Text style={TYPOGRAPHY.medium}>{formattedPrice}</Text>
        </View>
      )}
      {!isWeb && imageURLs && imageURLs.length > 0 ? (
        <ImageViewerModal
          imageURLs={imageURLs}
          initialIndex={activeIndex}
          isVisible={showViewer}
          onClose={() => {
            setShowViewer(false);
            setActiveIndex(0);
          }}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  flex: {
    flex: 1,
  },
  imagePreview: {
    borderRadius: theme.sk.radii.radiusMedium,
    height: 32,
    width: 32,
  },
  imagePreviews: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  mainContent: {
    flex: 1,
    flexDirection: 'row',
  },
  mainContentLarge: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  quantityBoxWrapper: {
    marginRight: 12,
  },
  quantityBoxWrapperLarge: {
    justifyContent: 'center',
    marginRight: 12,
  },
  subtitle: {
    flexShrink: 0,
  },
  subtitleLarge: {
    alignItems: 'center',
    flex: 2,
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: SCREEN_PADDING_LARGE,
  },
  titleContainer: {
    marginBottom: 16,
  },
  titleDescriptionContainer: {
    flex: 3,
    justifyContent: 'center',
  },
  totalPriceLarge: {
    alignItems: 'flex-end',
    flex: 1,
    justifyContent: 'center',
  },
}));
